import React from 'react';
import clsx from 'clsx';

// ─── StatusBadge ──────────────────────────────────────────────
const STATUS_MAP = {
  submitted: { label: 'Submitted', cls: 'badge-pending' },
  under_review: { label: 'Under Review', cls: 'badge-pending' },
  approved_by_related: { label: 'Dept Approved', cls: 'badge-approved' },
  objection_by_related: { label: 'Objection (Dept)', cls: 'badge-objection' },
  rejected_by_related: { label: 'Dept Rejected', cls: 'badge-rejected' },
  approved_final: { label: 'Final Approved ✓', cls: 'badge-approved' },
  objection_by_final: { label: 'Objection (Final)', cls: 'badge-objection' },
  rejected_final: { label: 'Finally Rejected', cls: 'badge-rejected' },
  pulled: { label: 'Pulled', cls: 'badge-pulled' },
  draft: { label: 'Draft', cls: 'badge-pulled' },
};

export const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || { label: status, cls: 'badge-pending' };
  return <span className={s.cls}>{s.label}</span>;
};

// ─── LoadingSpinner ───────────────────────────────────────────
export const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizes = { sm: 'h-4 w-4', md: 'h-8 w-8', lg: 'h-12 w-12' };
  return (
    <div className={clsx('flex items-center justify-center', className)}>
      <div className={clsx('animate-spin rounded-full border-2 border-blue-600 border-t-transparent', sizes[size])} />
    </div>
  );
};

// ─── Modal ────────────────────────────────────────────────────
export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={clsx('relative bg-white rounded-2xl shadow-2xl w-full', sizes[size])}>
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="p-6 max-h-[75vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

// ─── Table ────────────────────────────────────────────────────
export const Table = ({ columns, data, loading, emptyMsg = 'No records found' }) => {
  if (loading) return <LoadingSpinner className="py-12" />;
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="data-table">
        <thead>
          <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {!data?.length
            ? <tr><td colSpan={columns.length} className="text-center py-8 text-gray-400">{emptyMsg}</td></tr>
            : data.map((row, i) => (
              <tr key={row.id || i}>
                {columns.map(c => <td key={c.key}>{c.render ? c.render(row) : row[c.key]}</td>)}
              </tr>
            ))
          }
        </tbody>
      </table>
    </div>
  );
};

// ─── Stat Card ────────────────────────────────────────────────
export const StatCard = ({ title, value, icon, color = 'blue', sub }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    orange: 'bg-orange-100 text-orange-700',
    purple: 'bg-purple-100 text-purple-700',
  };
  return (
    <div className="stat-card">
      <div className={clsx('p-3 rounded-xl', colors[color])}>
        <span className="text-2xl">{icon}</span>
      </div>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-800">{value ?? 0}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

// ─── PageHeader ───────────────────────────────────────────────
export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-center justify-between mb-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    {actions && <div className="flex gap-2">{actions}</div>}
  </div>
);

// ─── Confirm Dialog ───────────────────────────────────────────
export const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message, confirmLabel = 'Confirm', danger = false }) => (
  <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
    <p className="text-gray-600 mb-6">{message}</p>
    <div className="flex gap-3 justify-end">
      <button className="btn-secondary" onClick={onClose}>Cancel</button>
      <button className={danger ? 'btn-danger' : 'btn-primary'} onClick={onConfirm}>{confirmLabel}</button>
    </div>
  </Modal>
);

// ─── FormField ────────────────────────────────────────────────
export const FormField = ({ label, error, children, required }) => (
  <div>
    <label className="form-label">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
    {children}
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

// ─── Empty State ──────────────────────────────────────────────
export const EmptyState = ({ icon = '📭', title, desc, action }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="text-6xl mb-4">{icon}</div>
    <h3 className="text-lg font-semibold text-gray-700 mb-2">{title}</h3>
    {desc && <p className="text-sm text-gray-400 mb-4">{desc}</p>}
    {action}
  </div>
);
